import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2, Eye, X } from 'lucide-react';
import { SiteMode } from '@meshsmith/shared';
import { useSiteStatus } from '../hooks/useSiteStatus';
import { clearPreviewToken } from '../lib/api';
import { ComingSoonScreen, MaintenanceScreen } from '../pages/SitePlaceholder';

/** Auth routes stay reachable even while the storefront is gated. */
const ALWAYS_ALLOWED = ['/login'];

export function SiteGate({ children }: { children: ReactNode }) {
  const { status, isLoading } = useSiteStatus();
  const { pathname } = useLocation();

  // First load: brief hold so we don't flash the storefront then swap it out.
  if (isLoading && !status) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-warm-off-white text-secondary-grey">
        <Loader2 className="animate-spin" size={22} />
      </div>
    );
  }

  const mode = status?.mode ?? SiteMode.LIVE;
  const bypass = status?.bypass ?? 'none';

  if (mode === SiteMode.LIVE) return <>{children}</>;
  if (ALWAYS_ALLOWED.some((p) => pathname.startsWith(p))) return <>{children}</>;

  if (bypass !== 'none') {
    return (
      <>
        <PreviewBanner mode={mode} via={bypass} />
        {children}
      </>
    );
  }

  return mode === SiteMode.COMING_SOON ? (
    <ComingSoonScreen content={status!.comingSoon} />
  ) : (
    <MaintenanceScreen content={status!.maintenance} />
  );
}

function PreviewBanner({ mode, via }: { mode: SiteMode; via: 'staff' | 'token' }) {
  const label = mode === SiteMode.COMING_SOON ? 'Coming soon' : 'Maintenance';
  return (
    <div className="sticky top-0 z-[60] flex items-center justify-center gap-2 bg-charcoal px-4 py-1.5 text-center text-[0.78rem] text-white">
      <Eye size={13} className="text-logo-from" />
      <span>
        Preview mode — the store is set to <strong>{label}</strong>. Only{' '}
        {via === 'staff' ? 'signed-in staff' : 'people with the preview link'} can see it.
      </span>
      {via === 'token' && (
        <button
          onClick={() => {
            clearPreviewToken();
            window.location.reload();
          }}
          className="ml-2 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-white/70 hover:bg-white/10 hover:text-white"
        >
          <X size={12} /> Exit
        </button>
      )}
    </div>
  );
}
